
import React from 'react';
import { format, parseISO } from 'date-fns';
import { Edit, Calendar, Home, Building2, Briefcase, PhoneCall, Mail, User, Key, ArrowUpRight } from 'lucide-react';
import { Subscription, Invoice } from '@/lib/data';
import StatusBadge from '@/components/ui/StatusBadge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import SubscriptionServices from './SubscriptionServices';
import InvoiceList from '@/components/invoices/InvoiceList';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/hooks/useLanguage';

interface SubscriptionDetailProps {
  subscription: Subscription;
  invoices: Invoice[];
  onRefresh: () => void;
}

const SubscriptionDetail: React.FC<SubscriptionDetailProps> = ({
  subscription,
  invoices,
  onRefresh,
}) => {
  const { translate } = useLanguage();
  
  const formatDate = (dateString: string | null) => {
    if (!dateString) return translate('subscriptions.notSet');
    try {
      return format(parseISO(dateString), 'PP');
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid date';
    }
  };

  const getHousingTypeIcon = () => {
    switch (subscription.housing_type) {
      case 'house':
        return <Home className="h-5 w-5 text-green-600" />;
      case 'apartment':
        return <Building2 className="h-5 w-5 text-blue-600" />;
      case 'office':
        return <Briefcase className="h-5 w-5 text-purple-600" />;
      default:
        return <Home className="h-5 w-5 text-muted-foreground" />;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">{subscription.subscription_number}</h1>
          <p className="text-muted-foreground">
            {translate('subscriptions.subscription')} {translate('subscriptions.for')} {subscription.customer.customer_name} ({subscription.customer.customer_number})
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={onRefresh} variant="outline" size="sm">
            {translate('subscriptions.refreshData')}
          </Button>
          <Link to={`/subscriptions/${subscription.subscription_id}/edit`}>
            <Button className="flex items-center gap-2">
              <Edit className="h-4 w-4" /> {translate('subscriptions.editSubscription')}
            </Button>
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>{translate('subscriptions.status')}</CardTitle>
            <CardDescription>{translate('subscriptions.currentSubscriptionStatus')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">{translate('subscriptions.status')}</span>
                <StatusBadge status={subscription.status} size="lg" />
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">{translate('subscriptions.registeredDate')}</span>
                <span>{formatDate(subscription.registered_at)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">{translate('subscriptions.activationDate')}</span>
                <span>{formatDate(subscription.activation_date)}</span>
              </div>
              {subscription.status === 'terminated' && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">{translate('subscriptions.terminationDate')}</span>
                  <span>{formatDate(subscription.termination_date)}</span>
                </div>
              )}
              {subscription.status === 'cancelled' && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">{translate('subscriptions.cancellationDate')}</span>
                  <span>{formatDate(subscription.cancellation_date)}</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{translate('subscriptions.billing')}</CardTitle>
            <CardDescription>{translate('subscriptions.billingInformation')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">{translate('subscriptions.lastBill')}</span>
                <span>{formatDate(subscription.last_bill_date)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">{translate('subscriptions.nextBill')}</span>
                <span className="flex items-center gap-1">
                  <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                  {formatDate(subscription.next_bill_date)}
                </span>
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">{translate('subscriptions.reference')}</span>
                <span>{subscription.invoice_reference || translate('subscriptions.none')}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{translate('subscriptions.installation')}</CardTitle>
            <CardDescription>{translate('subscriptions.installationDetails')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">{translate('subscriptions.type')}</span>
                <span className="flex items-center gap-2">
                  {getHousingTypeIcon()}
                  <span className="capitalize">{translate(`subscriptions.${subscription.housing_type}`)}</span>
                </span>
              </div>
              <Separator />
              <div className="flex flex-col gap-1">
                <span className="text-sm text-muted-foreground">{translate('subscriptions.address')}</span>
                <span className="font-medium">{subscription.installation_street}</span>
                <span>
                  {subscription.installation_zip_code} {subscription.installation_city}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>{translate('subscriptions.contactInformation')}</CardTitle>
            <CardDescription>{translate('subscriptions.primaryContactForInstallation')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <User className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div>
                  <p className="font-medium">{subscription.contact_name || translate('subscriptions.notSpecified')}</p>
                  <p className="text-sm text-muted-foreground">{translate('subscriptions.primaryContact')}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <PhoneCall className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div>
                  <p className="font-medium">{subscription.contact_phone || translate('subscriptions.notSpecified')}</p>
                  <p className="text-sm text-muted-foreground">{translate('subscriptions.phoneNumber')}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Mail className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div>
                  <p className="font-medium">{subscription.contact_email || translate('subscriptions.notSpecified')}</p>
                  <p className="text-sm text-muted-foreground">{translate('subscriptions.emailAddress')}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{translate('subscriptions.externalIdentifiers')}</CardTitle>
            <CardDescription>{translate('subscriptions.integrationWithExternalSystems')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <ArrowUpRight className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div>
                  <p className="font-medium">{subscription.alarm_com_id || translate('subscriptions.notConnected')}</p>
                  <p className="text-sm text-muted-foreground">{translate('subscriptions.alarmComId')}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Key className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div>
                  <p className="font-medium">{subscription.prom_avarn || translate('subscriptions.notSpecified')}</p>
                  <p className="text-sm text-muted-foreground">{translate('subscriptions.promAvarn')}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="services" className="w-full">
        <TabsList>
          <TabsTrigger value="services">{translate('subscriptions.services')}</TabsTrigger>
          <TabsTrigger value="invoices">{translate('subscriptions.invoices')}</TabsTrigger>
        </TabsList>
        <TabsContent value="services" className="border rounded-md p-4 mt-4">
          <SubscriptionServices subscription={subscription} onRefresh={onRefresh} />
        </TabsContent>
        <TabsContent value="invoices" className="border rounded-md p-4 mt-4">
          <h3 className="text-lg font-medium mb-4">{translate('subscriptions.invoices')}</h3>
          <InvoiceList invoices={invoices} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SubscriptionDetail;
